/**
 * Filtro "Somente Amazônia Legal" — parte pura (sem DOM), usada também no
 * Web Worker do diário. O carregamento dos dados fica em `amazoniaData.ts`.
 *
 * - Diário: pela coluna `municipio_id`, com a lista de códigos IBGE de
 *   `public/data/municipios_codigos.json`. Sem a lista, usa as UFs da
 *   Amazônia Legal (MA só a oeste de 44° O) dentro da caixa aproximada.
 * - 10 min (América do Sul, sem município): pelo polígono `amazonia_legal`
 *   (coordenadas do mapa); sem ele, pela caixa aproximada.
 */
import { AMAZONIA_LEGAL_BBOX_LONLAT } from '../map/projection';
import type { DailyAmazoniaTest } from './csv';

const [BBOX_W, BBOX_S, BBOX_E, BBOX_N] = AMAZONIA_LEGAL_BBOX_LONLAT as [number, number, number, number];

/** Caixa aproximada da Amazônia Legal [-74, -18.1, -43.9, 5.3]. */
export function inAmazoniaBbox(lon: number, lat: number): boolean {
  return lon >= BBOX_W && lon <= BBOX_E && lat >= BBOX_S && lat <= BBOX_N;
}

/** UFs inteiras na Amazônia Legal: RO, AC, AM, RR, PA, AP, TO, MT. */
const AMAZONIA_UFS = new Set([11, 12, 13, 14, 15, 16, 17, 51]);
const UF_MA = 21;

/** Critério aproximado por UF (usado enquanto a lista de municípios não existe). */
export function inAmazoniaByUf(ufCode: number | null, lon: number, lat: number): boolean {
  if (!inAmazoniaBbox(lon, lat)) return false;
  if (ufCode === null) return true;
  if (AMAZONIA_UFS.has(ufCode)) return true;
  return ufCode === UF_MA && lon <= -44;
}

/** Teste do diário: por código de município quando há lista; senão por UF + caixa. */
export function makeDailyAmazoniaTest(codes: ReadonlySet<number> | null): DailyAmazoniaTest {
  if (codes && codes.size > 0) return (cdMun) => cdMun !== null && codes.has(cdMun);
  return (_cdMun, uf, lon, lat) => inAmazoniaByUf(uf, lon, lat);
}

/** Converte a lista (texto ou número) em códigos numéricos válidos. */
export function parseMunicipioCodes(json: unknown): Set<number> | null {
  if (!Array.isArray(json)) return null;
  const codes = new Set<number>();
  for (const v of json) {
    const n = Number(typeof v === 'string' ? v.trim() : v);
    if (Number.isInteger(n) && n >= 1_000_000 && n <= 9_999_999) codes.add(n);
  }
  return codes.size > 0 ? codes : null;
}

/** Teste dos focos de 10 min: lon/lat (caixa) e x/y do mapa (polígono). */
export type TenMinAmazoniaTest = (lon: number, lat: number, x: number, y: number) => boolean;

/** Polígono quando houver; senão a caixa aproximada. */
export function makeTenMinAmazoniaTest(polygon: PolygonIndex | null): TenMinAmazoniaTest {
  if (polygon) return (_lon, _lat, x, y) => polygon.contains(x, y);
  return (lon, lat) => inAmazoniaBbox(lon, lat);
}

/**
 * Teste ponto-em-polígono (regra par-ímpar, aceita multipolígonos e buracos)
 * com as arestas agrupadas em faixas horizontais: cada consulta só olha as
 * arestas da sua faixa. Funciona em qualquer sistema plano (lon/lat ou
 * coordenadas do mapa).
 */
export class PolygonIndex {
  private readonly edges: Float64Array;
  private readonly bands: Uint32Array[];
  private readonly minLat: number;
  private readonly bandH: number;
  private readonly bandCount: number;
  readonly bbox: [number, number, number, number];

  constructor(rings: readonly (readonly number[][])[], bandCount = 512) {
    let n = 0;
    for (const r of rings) n += r.length;
    const edges = new Float64Array(n * 4);
    let e = 0;
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const r of rings) {
      for (let i = 0; i < r.length; i++) {
        const a = r[i]!;
        const b = r[(i + 1) % r.length]!;
        if (a[1] === b[1] && a[0] === b[0]) continue;
        edges[e++] = a[0]!;
        edges[e++] = a[1]!;
        edges[e++] = b[0]!;
        edges[e++] = b[1]!;
        minX = Math.min(minX, a[0]!);
        maxX = Math.max(maxX, a[0]!);
        minY = Math.min(minY, a[1]!);
        maxY = Math.max(maxY, a[1]!);
      }
    }
    this.edges = edges.subarray(0, e);
    this.bbox = [minX, minY, maxX, maxY];
    this.bandCount = bandCount;
    this.minLat = minY;
    this.bandH = Math.max((maxY - minY) / bandCount, 1e-9);
    const lists: number[][] = Array.from({ length: bandCount }, () => []);
    for (let i = 0; i < e; i += 4) {
      const y1 = this.edges[i + 1]!;
      const y2 = this.edges[i + 3]!;
      const b0 = this.band(Math.min(y1, y2));
      const b1 = this.band(Math.max(y1, y2));
      for (let b = b0; b <= b1; b++) lists[b]!.push(i);
    }
    this.bands = lists.map((l) => Uint32Array.from(l));
  }

  private band(lat: number): number {
    const b = Math.floor((lat - this.minLat) / this.bandH);
    return Math.max(0, Math.min(this.bandCount - 1, b));
  }

  contains(lon: number, lat: number): boolean {
    const [minX, minY, maxX, maxY] = this.bbox;
    if (lon < minX || lon > maxX || lat < minY || lat > maxY) return false;
    const list = this.bands[this.band(lat)]!;
    const E = this.edges;
    let inside = false;
    for (let k = 0; k < list.length; k++) {
      const i = list[k]!;
      const x1 = E[i]!, y1 = E[i + 1]!, x2 = E[i + 2]!, y2 = E[i + 3]!;
      if (y1 > lat !== y2 > lat && lon < ((x2 - x1) * (lat - y1)) / (y2 - y1) + x1) inside = !inside;
    }
    return inside;
  }
}
